import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useSocket } from './SocketContext';
import { useAuth } from './AuthContext';
import { callKitService } from '../services/CallKitService';

const CallContext = createContext();

export const useCall = () => useContext(CallContext);

export const CallProvider = ({ children }) => {
    const { socket } = useSocket();
    const { user } = useAuth();

    // Call State: 'IDLE', 'CALLING', 'RINGING', 'CONNECTING', 'ACTIVE', 'ENDED'
    const [callStatus, setCallStatus] = useState('IDLE');
    const [callData, setCallData] = useState(null); // { consultationId, callerId, etc. }
    const [remoteStream, setRemoteStream] = useState(null);
    const [localStream, setLocalStream] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);

    const peerRef = useRef(null);
    const socketRef = useRef(socket);
    const callDataRef = useRef(callData);
    const localStreamRef = useRef(localStream);
    const endCallRef = useRef(null);
    const callStatusRef = useRef(callStatus);

    useEffect(() => { callStatusRef.current = callStatus; }, [callStatus]);
    useEffect(() => { callDataRef.current = callData; }, [callData]);
    useEffect(() => { localStreamRef.current = localStream; }, [localStream]);
    useEffect(() => { socketRef.current = socket; }, [socket]);

    const endCall = (reason = 'Ended') => {
        const s = socketRef.current;
        const cd = callDataRef.current;
        const ls = localStreamRef.current;

        if (s && cd) {
            s.emit('end_call', { consultationId: cd.consultationId });
        }

        if (ls) {
            ls.getTracks().forEach(track => track.stop());
        }
        if (peerRef.current) {
            peerRef.current.destroy();
        }
        
        if (cd) {
            callKitService.endCall(cd.consultationId);
        }

        setLocalStream(null);
        setRemoteStream(null);
        setCallData(null);
        setCallStatus('IDLE');
        peerRef.current = null;
    };

    endCallRef.current = endCall;

    // --- Socket Event Listeners ---
    useEffect(() => {
        if (!socket) return;

        const handleCallIncoming = (data) => {
            if (callStatusRef.current === 'IDLE') {
                setCallStatus('RINGING');
                setCallData(data);
                if (data.callerName) {
                    callKitService.reportIncomingCall(data.consultationId, data.callerName, data.type === 'video');
                }
            } else {
                socket.emit('call_busy', { callerId: data.callerId });
            }
        };

        const handleCallAccepted = () => {
            setCallStatus('CONNECTING');
        };

        const handlePushCall = (e) => {
            const data = e.detail;
            if (callStatusRef.current === 'IDLE') {
                setCallStatus('RINGING');
                setCallData(data);
                if (data.callerName) {
                    callKitService.reportIncomingCall(data.consultationId, data.callerName, data.type === 'video');
                }
            }
        };

        const handleCallRejected = () => {
            if (endCallRef.current) endCallRef.current('Rejected');
        };

        const handleCallEndedRemote = () => {
            if (endCallRef.current) endCallRef.current('Remote Ended');
        };

        const handleCallKitAnswered = (callId) => {
            if (callDataRef.current && callDataRef.current.consultationId === callId) {
                // Wait for the UI component (like IncomingCallOverlay) to render and actually answer
                // Or we can programmatically trigger it:
                if (answerCallRef.current) answerCallRef.current();
            }
        };

        const handleCallKitEnded = (callId) => {
             if (endCallRef.current) endCallRef.current('CallKit Ended');
        };

        callKitService.on('callAnswered', handleCallKitAnswered);
        callKitService.on('callEnded', handleCallKitEnded);

        socket.on('call_incoming', handleCallIncoming);
        socket.on('call_accepted', handleCallAccepted);
        socket.on('call_rejected', handleCallRejected);
        socket.on('call_ended_remote', handleCallEndedRemote);
        window.addEventListener('push_call_incoming', handlePushCall);

        return () => {
            socket.off('call_incoming', handleCallIncoming);
            socket.off('call_accepted', handleCallAccepted);
            socket.off('call_rejected', handleCallRejected);
            socket.off('call_ended_remote', handleCallEndedRemote);
            window.removeEventListener('push_call_incoming', handlePushCall);
        };
    }, [socket]);

    // --- Actions ---

    const startCall = (consultationId, calleeId, type = 'video') => {
        if (!socket) return;
        setCallStatus('CALLING');
        setCallData({ consultationId, calleeId, type, isInitiator: true });

        socket.emit('call_user', {
            consultationId,
            calleeId,
            callerId: user._id,
            callerName: user.name,
            type
        });
    };

    const answerCall = () => {
        setCallStatus('CONNECTING');
        // Logic to trigger WebRTC connection...
        // We will emit 'call_accepted' to sender
        if (socket && callData) {
            socket.emit('call_accepted', {
                to: callData.callerId,
                consultationId: callData.consultationId
            });
        }
    };
    
    const answerCallRef = useRef(answerCall);
    useEffect(() => { answerCallRef.current = answerCall; }, [answerCall]);

    const toggleMute = () => {
        if (localStream) {
            localStream.getAudioTracks().forEach(track => track.enabled = !track.enabled);
            setIsMuted(!isMuted);
        }
    };

    const toggleVideo = () => {
        if (localStream) {
            localStream.getVideoTracks().forEach(track => track.enabled = !track.enabled);
            setIsVideoOff(!isVideoOff);
        }
    };

    return (
        <CallContext.Provider value={{
            callStatus,
            callData,
            localStream,
            remoteStream,
            isMuted,
            isVideoOff,
            startCall,
            answerCall,
            endCall,
            toggleMute,
            toggleVideo,
            setLocalStream,
            setRemoteStream,
            peerRef,
            socketRef
        }}>
            {children}
        </CallContext.Provider>
    );
};
